import {
  getCanonicalTreeTabScopedSanitizedEvidenceRegistry,
  type CanonicalTreeTabScopedEvidenceRegistrySnapshot,
} from './CanonicalTreeTabScopedSanitizedEvidenceRegistry';

import {
  readCanonicalTreeHistoricalAuthorityPresentationLifecycle,
  subscribeCanonicalTreeHistoricalAuthorityPresentationLifecycle,
  type CanonicalTreeHistoricalAuthorityPresentationLifecycleRecord,
  type CanonicalTreeHistoricalAuthorityPresentationLifecycleSnapshot,
} from './CanonicalTreeHistoricalAuthorityPresentationObservabilityLifecycle';

import {
  publishCanonicalTreeRetirementReadinessEvidence,
} from './CanonicalTreeHistoricalAuthorityPresentationObservabilityLifecycleRetirementReadinessAuthenticatedEvidencePublication';

import {
  getCanonicalTreeAuthenticatedEvidenceProductionObservationRegistry,
} from './CanonicalTreeHistoricalAuthorityPresentationObservabilityLifecycleRetirementReadinessAuthenticatedEvidenceProductionObservationRegistry';

import type {
  CanonicalTreeRetirementReadinessClientEvidenceKind,
} from './CanonicalTreeHistoricalAuthorityPresentationObservabilityLifecycleRetirementReadinessAuthenticatedEvidencePublicationArchitecture';

import {
  readCanonicalTreeBrowserBuildAndDeploymentIdentity,
} from './CanonicalTreeHistoricalAuthorityPresentationObservabilityLifecycleRetirementReadinessBrowserBuildAndDeploymentIdentity';

export type CanonicalTreeRetirementReadinessRuntimeCoordinatorStop =
  () => void;

const isProjectionAvailable =
  (
    snapshot:
      CanonicalTreeTabScopedEvidenceRegistrySnapshot,
  ): boolean =>
    snapshot.publisherLifecycle ===
      'PUBLISHER_ACTIVE' &&
    snapshot.evidenceAvailability ===
      'EVIDENCE_AVAILABLE' &&
    snapshot.authenticatedOwnerPresent ===
      true &&
    snapshot.summary !==
      null;

const mapLifecycleRecordToPublication =
  (
    record:
      CanonicalTreeHistoricalAuthorityPresentationLifecycleRecord,
  ): CanonicalTreeRetirementReadinessClientEvidenceKind | null => {
    if (
      record.eventKind ===
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_FALLBACK_ACTIVATED'
    ) {
      return 'FALLBACK_ACTIVATED';
    }

    if (
      record.eventKind ===
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_RENDER_FAILURE'
    ) {
      return 'RENDER_FAILURE';
    }

    return null;
  };

export const startCanonicalTreeRetirementReadinessAuthenticatedEvidencePublicationRuntimeCoordinator =
  (): CanonicalTreeRetirementReadinessRuntimeCoordinatorStop => {
    const browserIdentity =
      readCanonicalTreeBrowserBuildAndDeploymentIdentity();

    if (
      browserIdentity.status !==
        'AVAILABLE'
    ) {
      return () => {};
    }

    const registry =
      getCanonicalTreeTabScopedSanitizedEvidenceRegistry();

    const productionObservationRegistry =
      getCanonicalTreeAuthenticatedEvidenceProductionObservationRegistry();

    const abortController =
      new AbortController();

    let active =
      true;

    let publicationQueue:
      Promise<void> =
      Promise.resolve();

    let latestLifecycle =
      readCanonicalTreeHistoricalAuthorityPresentationLifecycle();

    let previousLifecycle:
      CanonicalTreeHistoricalAuthorityPresentationLifecycleSnapshot | null =
      null;

    let previousRegistry =
      registry.getSnapshot();

    const observedRecordIds =
      new Set(
        latestLifecycle.records.map(
          record =>
            record.recordId,
        ),
      );

    const enqueuePublication =
      (
        eventKind:
          CanonicalTreeRetirementReadinessClientEvidenceKind,

        lifecycle:
          CanonicalTreeHistoricalAuthorityPresentationLifecycleSnapshot,
      ): void => {
        if (
          !active ||
          abortController.signal.aborted
        ) {
          return;
        }

        publicationQueue =
          publicationQueue
            .then(
              async () => {
                if (
                  !active ||
                  abortController.signal.aborted
                ) {
                  return;
                }

                const result =
                  await publishCanonicalTreeRetirementReadinessEvidence(
                    {
                      eventKind,

                      buildId:
                        browserIdentity.buildId,

                      deploymentId:
                        browserIdentity.deploymentId,

                      lifecycleSchemaVersion:
                        2,

                      openEpisodeCount:
                        lifecycle.openEpisodeCount,

                      migrationCompleted:
                        lifecycle.migration.migrated,

                      quarantinePresent:
                        lifecycle.quarantine !==
                        null,

                      clientObservedAt:
                        new Date().toISOString(),
                    },
                    {
                      signal:
                        abortController.signal,
                    },
                  );

                if (
                  !active ||
                  abortController.signal.aborted
                ) {
                  return;
                }

                if (
                  result.status ===
                    'ACCEPTED' ||
                  result.status ===
                    'DUPLICATE_ACCEPTED'
                ) {
                  productionObservationRegistry.publishAccepted(
                    Object.freeze({
                      eventKind,

                      status:
                        result.status,

                      duplicate:
                        result.duplicate,

                      evidenceId:
                        result.evidenceId,

                      receivedAt:
                        result.receivedAt,

                      buildId:
                        browserIdentity.buildId,

                      deploymentId:
                        browserIdentity.deploymentId,

                      installationId:
                        result.installationId,

                      retirementAuthorized:
                        false,

                      observedAt:
                        new Date().toISOString(),
                    }),
                  );
                }
              },
            )
            .catch(
              () => {
                // Publication remains best effort and non-blocking.
              },
            );
      };

    enqueuePublication(
      'CONSUMER_DEPENDENCY_OBSERVED',
      latestLifecycle,
    );

    const unsubscribeLifecycle =
      subscribeCanonicalTreeHistoricalAuthorityPresentationLifecycle(
        snapshot => {
          if (
            !active ||
            abortController.signal.aborted
          ) {
            return;
          }

          latestLifecycle =
            snapshot;

          if (
            previousLifecycle ===
              null
          ) {
            previousLifecycle =
              snapshot;

            for (const record of snapshot.records) {
              observedRecordIds.add(
                record.recordId,
              );
            }

            enqueuePublication(
              'CLIENT_OBSERVED',
              snapshot,
            );

            return;
          }

          for (const record of snapshot.records) {
            if (
              observedRecordIds.has(
                record.recordId,
              )
            ) {
              continue;
            }

            observedRecordIds.add(
              record.recordId,
            );

            const eventKind =
              mapLifecycleRecordToPublication(
                record,
              );

            if (eventKind) {
              enqueuePublication(
                eventKind,
                snapshot,
              );
            }
          }

          if (
            previousLifecycle.migration.migrated ===
              false &&
            snapshot.migration.migrated ===
              true
          ) {
            enqueuePublication(
              'MIGRATION_COMPLETED',
              snapshot,
            );
          }

          if (
            previousLifecycle.quarantine !==
              null &&
            snapshot.quarantine ===
              null
          ) {
            enqueuePublication(
              'CORRUPTION_RECOVERED',
              snapshot,
            );
          }

          if (
            previousLifecycle.openEpisodeCount ===
              0 &&
            snapshot.openEpisodeCount >
              0
          ) {
            enqueuePublication(
              'OPEN_EPISODE_OBSERVED',
              snapshot,
            );
          }

          if (
            previousLifecycle.openEpisodeCount >
              0 &&
            snapshot.openEpisodeCount ===
              0
          ) {
            enqueuePublication(
              'OPEN_EPISODES_CLEARED',
              snapshot,
            );
          }

          previousLifecycle =
            snapshot;
        },
      );

    const unsubscribeRegistry =
      registry.subscribe(
        () => {
          if (
            !active ||
            abortController.signal.aborted
          ) {
            return;
          }

          const currentRegistry =
            registry.getSnapshot();

          if (
            !isProjectionAvailable(
              previousRegistry,
            ) &&
            isProjectionAvailable(
              currentRegistry,
            )
          ) {
            enqueuePublication(
              'PROJECTION_AVAILABLE',
              latestLifecycle,
            );
          }

          previousRegistry =
            currentRegistry;
        },
      );

    return (): void => {
      if (!active) {
        return;
      }

      active =
        false;

      abortController.abort();

      try {
        unsubscribeLifecycle();
      } catch {
        // Coordinator disposal remains best effort.
      }

      try {
        unsubscribeRegistry();
      } catch {
        // Coordinator disposal remains best effort.
      }
    };
  };

export const describeCanonicalTreeRetirementReadinessAuthenticatedEvidencePublicationRuntimeCoordinator =
  () =>
    Object.freeze({
      milestone:
        'CC-TREE-ARCH-101',

      authority:
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_LIFECYCLE_RETIREMENT_READINESS_AUTHENTICATED_EVIDENCE_PUBLICATION_RUNTIME_COORDINATOR_IMPLEMENTATION',

      decision:
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_LIFECYCLE_RETIREMENT_READINESS_AUTHENTICATED_EVIDENCE_PUBLICATION_RUNTIME_COORDINATOR_IMPLEMENTED',

      nonReactCoordinator:
        true,

      publicationMode:
        'BEST_EFFORT_NON_BLOCKING',

      sameDocumentLifecycleSubscription:
        true,

      pollingUsed:
        false,

      duplicateObservabilityProducerAdded:
        false,

      browserIdentityRequired:
        true,

      commitRefTransmitted:
        false,

      deploymentContextTransmitted:
        false,

      abortOnUnmount:
        true,

      postUnmountPublicationAllowed:
        false,

      acceptedProductionObservationPublished:
        true,

      skippedProductionObservationStored:
        false,

      failedProductionObservationStored:
        false,

      productionObservationScope:
        'TAB_MEMORY_ONLY',

      productionIngestionCertified:
        false,

      runtimeDecisionModified:
        false,

      runtimeGuardModified:
        false,

      operationalCertificationModified:
        false,

      retirementAuthorized:
        false,
    });
